import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, isPrimaryHost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KEY = "dl_ref";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const LOGO_URL = "/api/files/dreamlabs/migrated/logo-b3d7c5ce2ec74890a8f19a1940418963.png";
export const DEFAULT_BRAND = { name: "DreamLabs UK", logoUrl: LOGO_URL, tagline: "Lab-crafted syrup concentrates. Privacy-first. Crypto only.", accent: "" };

const readStoredRef = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "null");
    if (raw?.slug && Date.now() - (raw.ts || 0) < TTL_MS) return raw.slug;
  } catch {}
  return "";
};

// Tailwind's primary colour is an HSL triplet ("266 72% 66%"), so convert the partner's hex.
const hexToHsl = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "";
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const refFromUrl = () => {
  const { pathname, search } = window.location;
  const q = new URLSearchParams(search).get("ref");
  if (q) return q.trim().toLowerCase();
  const m = pathname.match(/^\/p\/([a-z0-9-]+)\/?$/i);
  return m ? m[1].toLowerCase() : "";
};

const Ctx = createContext({ partner: null, ref: "", brand: DEFAULT_BRAND, isPartner: false, hostPartner: false, ready: true });

const ORIGINAL_ICON = document.querySelector("link[rel~='icon']")?.getAttribute("href") || "";

export const PartnerProvider = ({ children }) => {
  const { user } = useAuth();
  const [ref, setRef] = useState(() => refFromUrl() || readStoredRef());
  const [partner, setPartner] = useState(null);
  // On a partner's own hostname (<slug>.<partner base domain>) the shop is locked to that partner.
  const [hostPartner, setHostPartner] = useState(false);
  const [pending, setPending] = useState(() => Boolean(refFromUrl() || readStoredRef()) || !isPrimaryHost);

  useEffect(() => {
    if (isPrimaryHost) return;
    api.get("/partner/resolve", { params: { host: window.location.hostname } })
      .then((r) => { setHostPartner(true); setRef(r.data.slug); })
      .catch(() => { setHostPartner(false); setPending(false); });
  }, []);

  useEffect(() => {
    if (!ref) { localStorage.removeItem(KEY); setPartner(null); if (isPrimaryHost) setPending(false); return; }
    localStorage.setItem(KEY, JSON.stringify({ slug: ref, ts: Date.now() }));
    api.get(`/partner/${ref}`).then((r) => setPartner(r.data)).catch(() => { setPartner(null); setRef(""); }).finally(() => setPending(false));
  }, [ref]);

  const staff = user?.role === "dropship" || user?.role === "admin";
  // Referral-tier partners keep DreamLabs branding: the ref still tags the order, nothing is rebranded.
  const isPartner = Boolean(partner && partner.white_label !== false && (hostPartner || !staff));

  const brand = useMemo(() => (isPartner ? {
    name: partner.brand_name,
    logoUrl: partner.logo_url || "",
    tagline: partner.tagline || "",
    accent: partner.accent_color || "",
  } : DEFAULT_BRAND), [isPartner, partner]);

  useEffect(() => { document.title = brand.name; }, [brand.name]);

  // Partner shops: any visible "DreamLabs" (static copy, CMS text, product names) becomes the partner's
  // shop name, and "dreamlabs.uk" their own host — so nothing on their site names the main brand.
  useEffect(() => {
    if (!isPartner) return;
    const name = brand.name;
    const host = (() => { try { return new URL(partner?.shop_url || window.location.origin).host; } catch { return window.location.host; } })();
    const esc = (v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // "Lulu's Flavours" + "DreamLabs Flavours" would read "Lulu's Flavours Flavours" — drop the echoed last word.
    const dup = new RegExp(`(${esc(name)})\\s+${esc(name.split(/\s+/).pop())}\\b`, "gi");
    const swap = (s) => s.replace(/DreamLabs(?:\s?UK)?/g, name).replace(/dreamlabs\.uk/gi, host).replace(dup, "$1");
    const has = (s) => /DreamLabs|dreamlabs\.uk/i.test(s || "");
    const ATTRS = ["alt", "title", "placeholder", "aria-label"];
    const fixText = (n) => { if (has(n.nodeValue)) n.nodeValue = swap(n.nodeValue); };
    const fixAttrs = (el) => { for (const a of ATTRS) { const v = el.getAttribute?.(a); if (has(v)) el.setAttribute(a, swap(v)); } };
    const walk = (root) => {
      if (root.nodeType === 3) return fixText(root);
      if (root.nodeType !== 1) return;
      fixAttrs(root);
      const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
      let n; while ((n = w.nextNode())) (n.nodeType === 3 ? fixText(n) : fixAttrs(n));
    };
    walk(document.body);
    const meta = document.querySelector("meta[name='description']");
    if (meta) meta.setAttribute("content", brand.tagline || `${name} — order online`);
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") fixText(m.target);
        else if (m.type === "attributes") fixAttrs(m.target);
        else m.addedNodes.forEach(walk);
      }
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    return () => obs.disconnect();
  }, [isPartner, brand.name, brand.tagline, partner?.shop_url]);
  useEffect(() => {
    const icon = document.querySelector("link[rel~='icon']");
    if (icon) icon.setAttribute("href", isPartner && brand.logoUrl ? brand.logoUrl : ORIGINAL_ICON);
  }, [isPartner, brand.logoUrl]);
  useEffect(() => {
    const root = document.documentElement;
    if (isPartner && brand.accent) root.style.setProperty("--primary", hexToHsl(brand.accent));
    else root.style.removeProperty("--primary");
  }, [isPartner, brand.accent]);

  const value = useMemo(() => ({ partner: isPartner ? partner : null, ref: isPartner ? ref : "", brand, isPartner, hostPartner, ready: !pending }), [isPartner, partner, ref, brand, hostPartner, pending]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const usePartner = () => useContext(Ctx);
export const useBrand = () => useContext(Ctx).brand;
